import React, { useMemo, useState } from 'react';
import { getConsentRecord, saveConsent, ConsentStatus } from '../utils/consent';
import { trackConsentUpdate } from '../utils/analytics';

interface CookieBannerProps {
  onConsentChange: (status: ConsentStatus) => void;
}

const CookieBanner: React.FC<CookieBannerProps> = ({ onConsentChange }) => {
  const initialConsent = useMemo(() => getConsentRecord(), []);
  const [consentStatus, setConsentStatus] = useState<ConsentStatus | null>(initialConsent?.status || null);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(initialConsent?.analytics ?? false);

  const applyConsent = (status: ConsentStatus) => {
    saveConsent(status);
    setConsentStatus(status);
    setAnalyticsEnabled(status === 'accepted');
    onConsentChange(status);
    trackConsentUpdate(status);
    setIsPreferencesOpen(false);
  };

  return (
    <>
      {!consentStatus && (
        <div className="fixed bottom-0 inset-x-0 z-50 bg-gray-900 text-white border-t border-gray-700 shadow-2xl">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
            <p className="text-sm text-gray-200 mb-3">
              Utilizamos cookies técnicas y, si lo aceptas, analítica de visitas para medir tráfico y procedencia (UTM/referrer).
              Puedes cambiarlo cuando quieras desde "Preferencias de cookies".
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => applyConsent('accepted')}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg text-sm font-medium"
              >
                Aceptar analítica
              </button>
              <button
                type="button"
                onClick={() => applyConsent('rejected')}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-medium"
              >
                Rechazar
              </button>
              <button
                type="button"
                onClick={() => setIsPreferencesOpen(true)}
                className="px-4 py-2 border border-gray-500 hover:border-gray-400 rounded-lg text-sm font-medium"
              >
                Configurar
              </button>
              <a href="#legal" className="px-4 py-2 text-sm underline text-gray-300 hover:text-white">
                Ver textos legales
              </a>
            </div>
          </div>
        </div>
      )}

      {consentStatus && (
        <button
          type="button"
          onClick={() => setIsPreferencesOpen(true)}
          className="fixed bottom-4 left-4 z-40 px-3 py-2 bg-white border border-gray-300 shadow rounded-lg text-xs text-gray-700 hover:bg-gray-100"
        >
          Preferencias de cookies
        </button>
      )}

      {isPreferencesOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Configuración de cookies</h3>
            <p className="text-sm text-gray-600 mb-6">
              Las cookies técnicas son obligatorias para que la web funcione. La analítica es opcional.
            </p>

            <div className="space-y-4">
              <div className="flex items-center justify-between border border-gray-200 rounded-lg p-3">
                <div>
                  <p className="font-medium text-gray-900">Cookies técnicas</p>
                  <p className="text-xs text-gray-500">Necesarias para recordar tu consentimiento.</p>
                </div>
                <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded-full">
                  Siempre activas
                </span>
              </div>

              <label className="flex items-center justify-between border border-gray-200 rounded-lg p-3 cursor-pointer">
                <div>
                  <p className="font-medium text-gray-900">Cookies de analítica</p>
                  <p className="text-xs text-gray-500">Miden visitas, páginas vistas, referrers y campañas UTM.</p>
                </div>
                <input
                  type="checkbox"
                  checked={analyticsEnabled}
                  onChange={(event) => setAnalyticsEnabled(event.target.checked)}
                  className="w-4 h-4"
                />
              </label>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => setIsPreferencesOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => applyConsent(analyticsEnabled ? 'accepted' : 'rejected')}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700"
              >
                Guardar preferencias
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieBanner;
